import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { User, Building2, Plus, Save, Edit2, Check, X } from 'lucide-react';
import AgentFormModal from '@/components/AgentFormModal';
import TagMultiSelect from '@/components/TagMultiSelect';

/**
 * Inline editable Tenant + Managing Agent card scoped to a single
 * property. Designed to be embedded on the Appraisal Overview tab so the
 * operator can capture tenant/agent details without bouncing out to
 * Property Details.
 *
 * Writes go through PATCH /properties/{prop_id} (whitelisted in
 * routes/properties.py). On save, calls onChanged() so the parent can
 * refresh.
 */
export default function PropertyTenantAgentCard({ propertyId, onChanged }) {
  const { api } = useAuth();
  const [prop, setProp] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [agents, setAgents] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      setProp(data);
      setForm({
        is_rental: !!data.is_rental,
        tenant_contact_name: data.tenant_contact_name || '',
        tenant_contact_number: data.tenant_contact_number || '',
        tenant_email: data.tenant_email || '',
        tenant_preferred_contact_method: data.tenant_preferred_contact_method || 'Text',
        real_estate_agent_id: data.real_estate_agent_id || '',
        agent_name: data.agent_name || '',
        agent_company: data.agent_company || '',
        agent_phone: data.agent_phone || '',
        agent_email: data.agent_email || '',
      });
    } catch { /* ignore */ }
  }, [api, propertyId]);

  const loadAgents = useCallback(async () => {
    try { const { data } = await api.get('/agents'); setAgents(Array.isArray(data) ? data : []); }
    catch { setAgents([]); }
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (editing) loadAgents(); }, [editing, loadAgents]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/properties/${propertyId}`, form);
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  /** Instant-save patch — used by the always-visible Rental Yes/No
   *  toggle so the user doesn't have to enter Edit mode + click Save
   *  just to flip the rental status. */
  const quickPatch = async (partial) => {
    try {
      await api.patch(`/properties/${propertyId}`, partial);
      await load();
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
  };

  if (!prop) return null;
  const agent = (agents || []).find(a => a._id === prop.real_estate_agent_id) || null;

  return (
    <div data-testid="property-tenant-agent-card" className="bg-white border border-[#E2E2DF] rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 uppercase tracking-[0.14em]">
          <User size={14} className="text-[#D35400]" /> Tenant &amp; Rental Agent
          {prop.is_rental && <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#D35400] bg-[#FFF7ED] border border-[#D35400]/30 rounded-full px-2 py-0.5 ml-1">Rental</span>}
        </h3>
        {!editing ? (
          <button data-testid="tenant-agent-edit" onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs font-semibold text-[#D35400] hover:text-[#A04000]">
            <Edit2 size={12} /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button data-testid="tenant-agent-cancel" onClick={() => { setEditing(false); load(); }} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-[#E2E2DF] hover:bg-slate-50">
              <X size={12} /> Cancel
            </button>
            <button data-testid="tenant-agent-save" onClick={save} disabled={saving} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-[#D35400] text-white font-semibold hover:bg-[#B84500] disabled:opacity-60">
              <Save size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Rental Yes/No — always visible, instant-save (no edit mode
          required). Mirrors RentalStatusInline UX so users don't get
          stuck wondering why the toggle didn't persist. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Rental status:</span>
        <button
          data-testid="tac-rental-yes-btn"
          onClick={() => quickPatch({ is_rental: true })}
          className={`px-3 py-1 text-xs font-bold rounded-md border transition-colors ${prop.is_rental
            ? 'bg-emerald-600 text-white border-emerald-600'
            : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-300'}`}
        >Yes (Rental)</button>
        <button
          data-testid="tac-rental-no-btn"
          onClick={() => quickPatch({ is_rental: false })}
          className={`px-3 py-1 text-xs font-bold rounded-md border transition-colors ${!prop.is_rental
            ? 'bg-slate-800 text-white border-slate-800'
            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
        >No (Owner-occupied)</button>
      </div>

      {/* Tenant block */}
      {(editing && form.is_rental) || (!editing && (prop.is_rental || prop.tenant_contact_name)) ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Tenant Name" testId="tac-tenant-name" editing={editing} value={form.tenant_contact_name} display={prop.tenant_contact_name} onChange={v => setForm({ ...form, tenant_contact_name: v })} />
            <Field label="Tenant Phone" testId="tac-tenant-phone" editing={editing} value={form.tenant_contact_number} display={prop.tenant_contact_number} onChange={v => setForm({ ...form, tenant_contact_number: v })} />
            <Field label="Tenant Email" testId="tac-tenant-email" editing={editing} value={form.tenant_email} display={prop.tenant_email} onChange={v => setForm({ ...form, tenant_email: v })} type="email" />
          </div>
          <TagMultiSelect
            scope="tenant"
            label="Tenant comments"
            value={prop.tenant_tags || []}
            onChange={(next) => quickPatch({ tenant_tags: next })}
          />
          <GeneralCommentBox
            scope="tenant"
            value={prop.tenant_comments || ''}
            onSave={(text) => quickPatch({ tenant_comments: text })}
            placeholder="Extra notes about the tenant (gate code, dog name, working hours…)"
          />
        </>
      ) : (
        <p className="text-xs text-slate-400 italic">Owner-occupied — no tenant.{editing && ' Tick the rental toggle above to add tenant details.'}</p>
      )}

      {/* Agent block */}
      <div className="pt-3 border-t border-[#F0EFEC]">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Building2 size={12} className="text-[#D35400]" /> Rental Agent</h4>
          {editing && (
            <button
              data-testid="tac-add-agent-btn"
              type="button"
              onClick={() => { setEditAgent(null); setShowAgentModal(true); }}
              className="flex items-center gap-1 text-[11px] font-semibold text-[#D35400] hover:text-[#A04000]"
            ><Plus size={11} /> New agent</button>
          )}
        </div>
        {editing ? (
          <select
            data-testid="tac-agent-select"
            value={form.real_estate_agent_id || ''}
            onChange={e => setForm({ ...form, real_estate_agent_id: e.target.value })}
            className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm bg-white"
          >
            <option value="">— No agent —</option>
            {(agents || []).map(a => (
              <option key={a._id} value={a._id}>
                {a.agent_no ? `${a.agent_no} · ` : ''}{a.company || a.name || a.agent_id}
                {a.name ? ` (${a.name})` : ''}
              </option>
            ))}
          </select>
        ) : agent ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <p className="text-slate-700"><span className="text-slate-400">Company:</span> <span className="font-semibold">{agent.company || '—'}</span></p>
            <p className="text-slate-700"><span className="text-slate-400">Contact:</span> {agent.name || '—'}</p>
            <p className="text-slate-700"><span className="text-slate-400">Phone:</span> {agent.phone || '—'}</p>
            <p className="text-slate-700"><span className="text-slate-400">Email:</span> {agent.email || '—'}</p>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">No agent linked.</p>
        )}
        <div className="mt-3">
          <TagMultiSelect
            scope="agent"
            label="Agent comments"
            value={prop.agent_tags || []}
            onChange={(next) => quickPatch({ agent_tags: next })}
          />
          <GeneralCommentBox
            scope="agent"
            value={prop.agent_comments || ''}
            onSave={(text) => quickPatch({ agent_comments: text })}
            placeholder="Extra notes about the agent (booking quirks, payment terms…)"
          />
        </div>
      </div>

      {showAgentModal && (
        <AgentFormModal
          open={true}
          agent={editAgent}
          onClose={() => setShowAgentModal(false)}
          onSaved={async (savedAgent) => {
            setShowAgentModal(false);
            await loadAgents();
            if (savedAgent?._id) setForm(f => ({ ...f, real_estate_agent_id: savedAgent._id }));
          }}
        />
      )}
    </div>
  );
}

function Field({ label, testId, editing, value, display, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 block mb-1">{label}</label>
      {editing ? (
        <input data-testid={testId} type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
      ) : (
        <p className="text-sm text-slate-800">{display || <span className="text-slate-400">—</span>}</p>
      )}
    </div>
  );
}

/**
 * Free-text "extra comment" textarea that instant-saves on blur. Used for
 * both tenant_comments and agent_comments. Keeps a local draft so each
 * keystroke isn't a network round-trip, but flushes on blur or Ctrl+Enter.
 */
function GeneralCommentBox({ scope, value, onSave, placeholder }) {
  const [draft, setDraft] = React.useState(value || '');
  React.useEffect(() => { setDraft(value || ''); }, [value]);
  const flush = () => {
    const next = (draft || '').trim();
    if (next !== (value || '').trim()) onSave?.(next);
  };
  return (
    <div data-testid={`${scope}-general-comment-wrapper`} className="mt-2">
      <label className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 block mb-1">
        Extra general comment
      </label>
      <textarea
        data-testid={`${scope}-general-comment`}
        rows={2}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={flush}
        onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') flush(); }}
        placeholder={placeholder || ''}
        className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm leading-relaxed resize-y min-h-[44px]"
      />
      <p className="text-[10px] text-slate-400 mt-0.5">Saves when you click away or press Ctrl+Enter.</p>
    </div>
  );
}
